import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const AdminBibleCard = ({ product, onEdit = () => {}, searchQuery = '', highlightSearchTerm = null }) => {
  const handleEdit = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onEdit(product);
  };

  const getDisplayPrice = (product) => {
    // Si tiene ambos (forro y pestañas), mostrar total con ambos
    if (product.has_cover && product.has_tab_index && product.total_with_both) {
      return product.total_with_both;
    }
    // Si solo tiene forro, mostrar total con forro
    if (product.has_cover && product.total_with_cover) {
      return product.total_with_cover;
    }
    // Si solo tiene pestañas, mostrar total con pestañas
    if (product.has_tab_index && product.total_with_tab_index) {
      return product.total_with_tab_index;
    }
    // Si no tiene ninguno, mostrar precio base
    return product.price_bible;
  };

  if (!product) {
    return (
      <div className="animate-pulse bg-muted rounded-lg p-4 h-full flex flex-col space-y-3">
        <div className="bg-muted-foreground/20 rounded aspect-[3/4] w-full" />
        <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
        <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
        <div className="h-3 bg-muted-foreground/10 rounded w-2/3" />
      </div>
    );
  }

  return (
    <div onClick={handleEdit} className="group block cursor-pointer">
      <div className="bg-card rounded-lg shadow-subtle hover:shadow-modal transition-gentle hover-lift overflow-hidden h-full flex flex-col relative">
        <div className="absolute top-2 right-2 z-20">
          <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            Biblia
          </div>
        </div>

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
            <div className="flex items-center gap-2 text-slate-700">
              <Icon name="Edit" size={16} />
              <span className="text-sm font-medium">Editar biblia</span>
            </div>
          </div>
        </div>

        <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0 flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div className="relative w-[60px] h-[80px] sm:w-[120px] sm:h-[170px] flex items-center justify-center">
            <Image
              src={product.image_urls?.[0] || '/placeholder.jpg'}
              alt={`Portada de ${product.title_bible}`}
              className="w-full h-full object-cover rounded shadow-lg"
              low
            />
          </div>
          {product.is_new_bible && (
            <div className="absolute top-2 left-2 bg-success text-success-foreground text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
              Nuevo
            </div>
          )}
        </div>

        <div className="p-2 sm:p-3 flex-1 flex flex-col">
          <h3 className="font-heading font-bold text-base sm:text-lg text-card-foreground line-clamp-2 mb-1 group-hover:text-primary transition-smooth flex-1">
            {highlightSearchTerm && searchQuery ? 
              highlightSearchTerm(product.title_bible && product.title_bible.length > 25 ? `${product.title_bible.substring(0, 25)}...` : product.title_bible, searchQuery) :
              (product.title_bible && product.title_bible.length > 25 ? `${product.title_bible.substring(0, 25)}...` : product.title_bible)
            }
          </h3>
          <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
            Versión: {highlightSearchTerm && searchQuery ? 
              highlightSearchTerm(product.version_bible || 'Versión no especificada', searchQuery) :
              (product.version_bible || 'Versión no especificada')
            }
          </p>
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-xs sm:text-sm text-primary">
                ${getDisplayPrice(product)?.toFixed(2) || 'N/A'}
              </span>
            </div>
            {product.rating_product && (
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={10} className="text-warning fill-current sm:w-3 sm:h-3" />
                <span className="text-xs text-muted-foreground">
                  {product.rating_product?.toFixed(1)}
                </span>
              </div>
            )}
          </div>

          <div className="mb-2 flex-shrink-0 space-y-1">
            {/* Mobile: Stack vertically */}
            <div className="flex flex-col space-y-2 sm:hidden">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {product.language_bible && (
                  <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                    {product.language_bible}
                  </span>
                )}
              </div>
              
              <div className={`px-2 py-1 rounded-full text-xs font-medium self-start ${
                product.available_bible === true
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-destructive/10 text-destructive border border-destructive/20'
              }`}>
                {product.available_bible === true ? 'Disponible' : 'No disponible'}
              </div>
            </div>

            {/* Desktop: Side by side */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {product.language_bible && (
                  <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                    {product.language_bible}
                  </span>
                )}
                {product.format_bible && (
                  <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">
                    {product.format_bible}
                  </span>
                )}
              </div>
              
              <div className="flex-1"></div>
              
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                product.available_bible === true
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-destructive/10 text-destructive border border-destructive/20'
              }`}>
                {product.available_bible === true ? 'Disponible' : 'No disponible'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBibleCard;

